import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { BodyText, StepCard, StepTitle } from '../../components/ui'
import { MISC_HANDLED_STATES, miscStateName } from '../../data/miscHandledStates'
import type { BusinessEntity } from '../../types/onboarding'

/**
 * States screen (MISC Onboarding Sync): one question only — what states do you want MISC to handle?
 * Whether the entity is writing business in each state is asked later, on the credentials screen.
 */
export function EntityStates({ entity }: { entity: BusinessEntity }) {
  const { dispatch } = useOnboarding()
  const selected = new Set(entity.statesToHandle)

  const setStates = (states: string[]) => dispatch({ type: 'SET_ENTITY_STATES', entityId: entity.id, states })
  const toggle = (code: string) =>
    setStates(selected.has(code) ? entity.statesToHandle.filter((s) => s !== code) : [...entity.statesToHandle, code])

  return (
    <StepCard>
      <StepTitle subtitle={`Select every state where MISC will handle surplus lines tax filings and reporting for ${entity.name || 'this entity'}.`}>
        What state(s) do you want MISC to handle?
      </StepTitle>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button type="button" size="small" variant="outlined" onClick={() => setStates(MISC_HANDLED_STATES.map((s) => s.code))}>
          Select All
        </Button>
        <Button type="button" size="small" onClick={() => setStates([])}>
          Clear
        </Button>
        <Typography sx={{ ml: 'auto', fontSize: 13, fontWeight: 600, color: '#334155' }}>
          {entity.statesToHandle.length} selected
        </Typography>
      </Box>

      <Box
        role="group"
        aria-label="States for MISC to handle"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
          columnGap: 1,
          rowGap: 0,
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        {MISC_HANDLED_STATES.map((s) => (
          <FormControlLabel
            key={s.code}
            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
            control={<Checkbox size="small" checked={selected.has(s.code)} onChange={() => toggle(s.code)} />}
            label={s.name}
          />
        ))}
      </Box>

      {entity.statesToHandle.length > 0 ? (
        <BodyText sx={{ mt: 2, fontSize: 14, color: '#475569' }}>
          <strong>Selected:</strong> {entity.statesToHandle.map(miscStateName).join(', ')}
        </BodyText>
      ) : null}
    </StepCard>
  )
}
